import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { connect } from 'react-redux';
import { Form, Row, ProgressBar, FormText } from 'react-bootstrap';
import Dropzone from 'react-dropzone';
import EXIF from 'exif-js';

import { injectIntl } from 'react-intl';
import Notification from '../../Notification';
import Image from '../../Image';
import LoadingIndicator from '../../LoadingIndicator';
import { imageUpload } from '../../../actions';
import { isCloudinaryHostedImage } from '../../../utils/ImageUtils';
import VerbButton, { VERB_BUTTON_TYPE } from '../../VerbButton';

window.EXIF = EXIF; // Needed for exif parsing in croppie

class RawImageUpload extends Component {
    constructor(props) {
        super(props);
        this.state = {
            img: '',
            imageLoaded: false,
            imageSaved: false,
            imageUrl: '',
            loading: false
        };
    }

    static propTypes = {
        imageUpload: PropTypes.func.isRequired,
        disableClick: PropTypes.bool,
        displayLabel: PropTypes.bool,
        label: PropTypes.string,
        input: PropTypes.object.isRequired,
        onImageUpload: PropTypes.func,
        className: PropTypes.string,
        intl: PropTypes.object,
        imageProps: PropTypes.object,
        squareImage: PropTypes.any,
        styleProps: PropTypes.object,
        meta: PropTypes.object,
        disabled: PropTypes.bool,
        description: PropTypes.string,
        inputClassName: PropTypes.string,
        showDeleteButton: PropTypes.bool
    };

    componentDidMount() {
        this.setState({
            imageUrl: this.props.input.value
        });
    }

    componentDidUpdate(prevProps) {
        if (this.props.input.value !== prevProps.input.value) {
            this.setState({
                imageUrl: this.props.input.value
            });
        }
    }

    _transformUrl = (imageUrl) => {
        const parser = document.createElement('a');
        parser.href = imageUrl;

        let optimizedSource = imageUrl;
        if (isCloudinaryHostedImage(parser.hostname)) {
            // replaces all http with https images
            optimizedSource = optimizedSource.replace('http:', 'https:');
        }
        return optimizedSource;
    };

    _removeImage = () => {
        this.props.input.onChange('');
        this.setState({ imageUrl: '', imageLoaded: true, imageSaved: true });
        this.setState({
            imageSaved: false,
            imageLoaded: false,
            loading: false
        });
    };

    _saveImage = (image) => {
        const reader = new FileReader();
        this.setState({ loading: true });
        reader.readAsArrayBuffer(image);
        reader.onloadend = () => {
            const file = reader.result;
            const img = {
                file,
                name: image.name,
                type: image.type
            };
            this.props
                .imageUpload(img)
                .then((result) => {
                    this.props.input.onChange(result.url);
                    return result.url;
                })
                .then((resultUrl) => {
                    if (this.props.onImageUpload) {
                        const imageUrl = this._transformUrl(resultUrl);
                        this.props.onImageUpload(imageUrl);
                    }
                    this.setState({ imageUrl: resultUrl, imageLoaded: true, imageSaved: true });
                    setTimeout(() => {
                        this.setState({
                            imageSaved: false,
                            imageLoaded: false,
                            loading: false
                        });
                    }, 1000);
                });
        };
    };

    onOpenClick = () => {
        this.dropzone.open();
    };

    onDrop = (files) => {
        const file = files[0];
        this.setState({
            img: {
                name: file.name,
                type: file.type
            }
        });
        this._saveImage(file);
    };

    getFeedbackMessage = () => {
        const { meta = {} } = this.props;
        const { touched, error, warning } = meta;
        let message = '';
        if (touched) {
            if (error) {
                message = error;
            } else if (warning) {
                message = warning;
            }
        }
        return message;
    };

    render() {
        const {
            intl,
            label,
            input,
            className,
            squareImage,
            imageProps = {},
            disableClick = true,
            description,
            displayLabel = true,
            styleProps = {},
            inputClassName,
            disabled,
            showDeleteButton
        } = this.props;
        const { imageUrl, loading, imageSaved } = this.state;
        const { horizontal } = styleProps;
        let labelSection = null;
        if (displayLabel) {
            if (label || description) {
                labelSection = (
                    <div className={horizontal ? 'col-12 col-sm-6 no-padding' : ''}>
                        <div className={horizontal ? 'col-12 col-sm-10 no-padding' : ''}>
                            <label htmlFor={input.name} className={horizontal ? 'form-label' : ''}>
                                {label}
                            </label>
                            <p className={horizontal ? 'form-description' : ''}>{description}</p>
                        </div>
                    </div>
                );
            }
        }
        const helpBlock = (
            <div className={'row'}>
                <div className={'col-6 col-offset-6 no-padding'}>
                    <FormText>{this.getFeedbackMessage()}</FormText>
                </div>
            </div>
        );
        return (
            <Form.Group className={cns('form-image', className)}>
                {/* <div style={{ float: 'left', height: imageUrl ? '250px' : '100px' }} /> */}
                {labelSection}

                <Dropzone
                    className={cns('mx-auto', 'dropzone-input-raw', inputClassName)}
                    ref={(node) => {
                        this.dropzone = node;
                    }}
                    onDrop={this.onDrop}
                    disableClick={disableClick}
                    accept={'image/*'}
                >
                    {
                        (/* {isDragActive, isDragReject} */) => (
                            <Row className={cns('image-upload-container', { default: !imageUrl })}>
                                {!loading && (
                                    <Image
                                        className={cns('mx-auto', { square: squareImage })}
                                        src={imageUrl}
                                        dimension={imageProps.dimension}
                                        cloudinaryCrop={imageProps.cloudinaryCrop}
                                    />
                                )}
                                <ProgressBar
                                    now={imageSaved ? 100 : 0}
                                    className={cns({ hidden: !loading }, 'image-loading-bar')}
                                />
                                {loading ? <LoadingIndicator className={'image-loading-indicator'} /> : null}
                                <br />
                                {!disabled ? (
                                    <div className={cns('col-12', 'text-center')}>
                                        <VerbButton
                                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                                            onClick={this.onOpenClick}
                                            fontAwesomeIcon={imageUrl ? 'fa-pencil' : 'fa-plus'}
                                            textId={imageUrl ? 'BUTTON_CHANGE_IMAGE' : 'BUTTON_ADD_IMAGE'}
                                            defaultMessage={imageUrl ? 'Change Image' : 'Add Image'}
                                            className={'mx-auto'}
                                        />
                                    </div>
                                ) : null}
                                {imageUrl && showDeleteButton && (
                                    <div className={cns('col-12', 'text-center')}>
                                        <VerbButton
                                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                                            onClick={() => this._removeImage()}
                                            className={'delete-button mx-auto '}
                                            fontAwesomeIcon={'fa-trash'}
                                            textId={'DELETE_IMAGE'}
                                            defaultMessage={'Delete Image'}
                                        />
                                    </div>
                                )}
                                <span className={cns('mx-auto col-12', 'text-center')}>
                                    <Notification
                                        show={imageSaved}
                                        displayText={intl.formatMessage(
                                            { id: 'IMAGE_SAVED', defaultMessage: 'Image Saved!' },
                                            {}
                                        )}
                                    />
                                </span>
                            </Row>
                        )
                    }
                </Dropzone>
                {helpBlock}
            </Form.Group>
        );
    }
}
const mapStateToProps = (/* state, ownProps */) => ({});

const mapDispatchToProps = (dispatch) => ({
    imageUpload: (params) => dispatch(imageUpload(params))
});
export const RawImageUploadComponent = RawImageUpload;
export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(RawImageUpload));
